import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { BarLoader } from 'react-spinners';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { PiDownloadSimple } from 'react-icons/pi';
import { BsFiletypeCsv } from 'react-icons/bs';
import { LiaFileExcel } from 'react-icons/lia';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { RiRefreshLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import './reports.css';
import {
    DATE_API, PAGE_SIZES, DEFAULT_PAGE_SIZE, APPROVAL_OPTIONS, DELIVERY_OPTIONS,
    fmtMoney, fmtDate, DeliveryBadge, ApprovalBadge, toDateObj, TextField, SelectField, exportRows,
} from './purchaseOrderAdvanced';

// One row per PO. Same filters, dropdowns and formatting as Purchase Order Details.
const COLS = [
    { header: 'PO No', field: 'lpoNo', width: 130 },
    { header: 'PO Date', field: 'lpoDate', width: 120, date: true },
    { header: 'Supplier', field: 'supplier', width: 240 },
    { header: 'Supplier Account No', field: 'supplierAccountNo', width: 170 },
    { header: 'Gross', field: 'grossAmount', width: 130, money: true },
    { header: 'Discount', field: 'discountAmount', width: 130, money: true },
    { header: 'Net', field: 'netAmount', width: 130, money: true },
    { header: 'VAT', field: 'vatAmount', width: 120, money: true },
    { header: 'Total', field: 'totalAmount', width: 140, money: true },
    { header: 'Prepared By', field: 'preparedBy', width: 140 },
    { header: 'Reviewed By', field: 'reviewedBy', width: 140 },
    { header: 'Approved By', field: 'approvedBy', width: 140 },
    { header: 'Approval Status', field: 'approvalStatus', width: 150, approval: true },
    { header: 'Delivery Status', field: 'deliveryStatus', width: 180, delivery: true },
    { header: 'Invoice Status', field: 'invoiceStatus', width: 150 },
];
const SUM_FIELDS = ['grossAmount', 'discountAmount', 'netAmount', 'vatAmount', 'totalAmount'];
const EXPORT_CFG = {
    cols: COLS,
    sumFields: SUM_FIELDS,
    title: 'Purchase Order Status List',
    sheet: 'Purchase Order Status',
    file: 'Purchase_Order_Status',
};

const emptyFilters = () => ({
    poNo: '',
    vendor: null,
    fromDate: moment().format(DATE_API),
    toDate: moment().format(DATE_API),
    approval: APPROVAL_OPTIONS[0],
    delivery: DELIVERY_OPTIONS[0],
});

// Empty values are omitted so the backend treats them as "no filter".
const fetchPurchaseOrderStatus = async (apiService, filters, pageNo, pageSize) => {
    const params = new URLSearchParams();
    const add = (key, value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            params.append(`inventoryPOStatusRequest.${key}`, String(value).trim());
        }
    };
    add('POrderNo', filters.poNo);
    add('VendorName', filters.vendor?.label);
    add('FromDate', filters.fromDate);
    add('ToDate', filters.toDate);
    add('Status', filters.approval?.value);
    add('Filter', filters.delivery?.value);
    if (pageSize) {
        params.append('PageNo', pageNo || 1);
        params.append('PageSize', pageSize);
    }
    const res = await apiService.getApiParamater('GET_PO_STATUS_REPORT', `?${params.toString()}`);
    const body = res?.data;
    // 204 = no rows, not an error
    if (!body || body.statusCode === 204) return { rows: [], pagination: null };
    if (body.statusCode && body.statusCode >= 400) throw new Error(body.message || 'Request failed');
    const list = body.result ?? body.data;
    return { rows: Array.isArray(list) ? list : [], pagination: body.paginationData || null };
};

const POStatusFilterBar = ({ suppliers, onApply, onReset }) => {
    const [f, setF] = useState(emptyFilters());
    const set = (key) => (value) => setF((p) => ({ ...p, [key]: value }));
    const dateError = f.toDate && !f.fromDate ? 'Pick a From date — a To date on its own is ignored.' : '';

    const apply = () => {
        if (dateError) {
            toast.warning(dateError);
            return;
        }
        onApply(f);
    };
    const reset = () => {
        setF(emptyFilters());
        onReset();
    };

    return (
        <div className="gridBg m-0 mb-3 p-3" style={{ borderRadius: 16 }}>
            <div className="row">
                <TextField label="PO No" value={f.poNo} onChange={set('poNo')} />
                <SelectField label="Supplier" value={f.vendor} onChange={set('vendor')} isClearable placeholder="All"
                    options={(suppliers || []).map((x) => ({ value: x, label: x?.supplierName }))} />
                <SelectField label="Approval Status" value={f.approval} onChange={(o) => set('approval')(o || APPROVAL_OPTIONS[0])} options={APPROVAL_OPTIONS} />
                <SelectField label="Delivery Status" value={f.delivery} onChange={(o) => set('delivery')(o || DELIVERY_OPTIONS[0])} options={DELIVERY_OPTIONS} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">From Date</label>
                    <DatePicker selected={toDateObj(f.fromDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="From"
                        onChange={(d) => set('fromDate')(d ? moment(d).format(DATE_API) : '')} />
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">To Date</label>
                    <DatePicker selected={toDateObj(f.toDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="To" minDate={toDateObj(f.fromDate)}
                        onChange={(d) => set('toDate')(d ? moment(d).format(DATE_API) : '')} />
                    {dateError && <div className="small text-danger">{dateError}</div>}
                </div>
                <div className="col-md-3 col-sm-6 mb-2 d-flex align-items-end gap-2">
                    <div className="mainbtnGry flex-end gap-2 w-50" role="button" tabIndex={0} onClick={apply}
                        onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}>
                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Apply</div>
                    </div>
                    <div className="mainbtnRed flex-end gap-2 w-50" role="button" tabIndex={0} onClick={reset}
                        onKeyDown={(e) => { if (e.key === 'Enter') reset(); }}>
                        <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Reset</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const POStatusGrid = ({ rows }) => {
    const colDefs = useMemo(() => COLS.map((c, i) => {
        const def = {
            headerName: c.header,
            field: c.field,
            width: c.width,
            suppressMovable: true,
            filter: true,
            floatingFilter: true,
            ...(i === 0 ? { pinned: 'left' } : {}),
            ...(c.money ? { type: 'rightAligned', headerClass: 'ag-right-aligned-header' } : {}),
        };
        if (c.money) def.valueFormatter = (p) => fmtMoney(p.value);
        else if (c.date) def.valueFormatter = (p) => fmtDate(p.value);
        else if (c.delivery) def.cellRenderer = (p) => <DeliveryBadge value={p.value} />;
        else if (c.approval) def.cellRenderer = (p) => <ApprovalBadge value={p.value} />;
        return def;
    }), []);

    return (
        <div className="ag-theme-quartz" style={{ height: 560 }}>
            <AgGridReact
                rowData={rows}
                columnDefs={colDefs}
                getRowStyle={(p) => ({ background: p.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                suppressMovableColumns
            />
        </div>
    );
};

const PurchaseOrderStatus = () => {
    const apiService = useRef(new ApiService()).current;
    const reqId = useRef(0);
    const [filters, setFilters] = useState(emptyFilters());
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        apiService.get('GET_SUPPLIER_KANOO')
            .then((res) => setSuppliers(res?.data?.result || []))
            .catch((e) => console.error('Error loading suppliers:', e));
    }, [apiService]);

    // Fetches on mount, Apply and page / page-size change only. Stale responses are dropped.
    useEffect(() => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        fetchPurchaseOrderStatus(apiService, filters, pageNo, pageSize)
            .then(({ rows: r, pagination: p }) => {
                if (id !== reqId.current) return;
                setRows(r);
                setPagination(p);
            })
            .catch((e) => {
                if (id !== reqId.current) return;
                setRows([]);
                setPagination(null);
                setError(e?.message || 'Unable to load the report.');
            })
            .finally(() => { if (id === reqId.current) setLoading(false); });
    }, [apiService, filters, pageNo, pageSize]);

    const handleApply = (f) => { setFilters({ ...f }); setPageNo(1); };
    const handleReset = () => { setFilters(emptyFilters()); setPageNo(1); };
    // Export covers every matching row: one request with PageNo 1 and PageSize = total records.
    const handleExport = async (type) => {
        const total = pagination?.totalRecords || rows.length;
        if (!total) return;
        setExporting(true);
        try {
            const { rows: all } = await fetchPurchaseOrderStatus(apiService, filters, 1, total);
            exportRows(all, type, EXPORT_CFG);
        } catch (e) {
            toast.error(e?.message || 'Unable to export the report.');
        } finally {
            setExporting(false);
        }
    };
    const handlePageSize = (e) => { setPageSize(Number(e.target.value)); setPageNo(1); };

    return (
        <div className="p-4">
            <POStatusFilterBar suppliers={suppliers} onApply={handleApply} onReset={handleReset} />

            <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                <div className="mainInvHead center">Purchase Order Status</div>
                <div className="ms-auto">
                    <select className="form-select form-select-sm" aria-label="Page size" value={pageSize} onChange={handlePageSize}>
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
                <div className="mainbtnGry flex-end gap-2">
                    <Dropdown isOpen={exportOpen} toggle={() => setExportOpen(!exportOpen)}>
                        <DropdownToggle tag="span" aria-expanded={exportOpen} role="button" aria-label="Export">
                            <PiDownloadSimple size={25} />
                        </DropdownToggle>
                        <DropdownMenu className="mt-3">
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('csv')} className="gap-3 d-flex center"><BsFiletypeCsv /><div>Export CSV</div></DropdownItem>
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('xlsx')} className="gap-3 d-flex center"><LiaFileExcel /><div>Export Excel</div></DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>

            <div className="gridshad">
                {loading ? (
                    <div className="center p-5"><BarLoader color="#6E5199" height={3} width={200} /></div>
                ) : error ? (
                    <div className="text-danger p-4" role="alert">{error}</div>
                ) : !rows.length ? (
                    <div className="text-muted p-4">No purchase orders found.</div>
                ) : (
                    <POStatusGrid rows={rows} />
                )}
                {pagination && <Pagination pagination={pagination} getItems={(p) => setPageNo(p)} />}
            </div>
        </div>
    );
};

export default PurchaseOrderStatus;
